import React, { useState, useEffect, useMemo } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import AlertBox from '../components/AlertBox';
import './Pages.css';

function ManageOrganizations() {
  const { getAccessTokenSilently, isLoading: authLoading } = useAuth0();
  const [organizations, setOrganizations] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  // const [error, setError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);
  const [confirmationButtonType, setConfirmationButtonType] = useState(false);
  const [alertType, setAlertType] = useState<'alert' | 'error'>('alert');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(`/api/orgs`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
        });

        const isJson = res.headers.get('content-type')?.includes('application/json');
        const responseBody = isJson ? await res.json() : null;

        if (!res.ok) {
          const message =
            responseBody?.error ||
            responseBody?.fullError?.name ||
            res.statusText ||
            'Unknown error';
          throw new Error(`${message}`);
        }

        setOrganizations(responseBody);
      } catch (error) {
        console.error('Error fetching organization:', error);
        setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
        setConfirmationButtonType(true);
        setAlertType('error');
        setShowAlert(true);
      } finally {
        setLoading(false);
      }
    };

    if (!authLoading) {
      fetchOrganizations();
    }
  }, [getAccessTokenSilently, authLoading]);

  const handleAlertYes = async () => {
    setShowAlert(false);
    setAlertMessage(null);
    setConfirmationButtonType(true);
    setAlertType('alert');

    const orgId = selectedOrgId;
    if (!orgId) {
      setAlertMessage('No organization selected for token regeneration.');
      setShowAlert(true);
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const url = `/api/orgs/${orgId}/grafana-token`;
      const method = 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
      });

      const isJson = res.headers.get('content-type')?.includes('application/json');
      const data = isJson ? await res.json() : null;

      if (!res.ok) {
        const message =
          data?.error ||
          data?.fullError?.name ||
          res.statusText ||
          'Unknown error';
        throw new Error(`${message}`);
      }

      setAlertMessage(`Token regenerated successfully!\nService Account: ${data.serviceAccount}\nService Token: ${data.serviceToken}`);
      setShowAlert(true);
    } catch (error) {
      console.error('Error regenerating token:', error);
      setAlertMessage(error instanceof Error ? error.message : 'Unknown error occurred');
      setConfirmationButtonType(true);
      setAlertType('error');
      setShowAlert(true);
    } finally {
      setSelectedOrgId(null);
    }
  };

  const handleAlertNo = () => {
    setShowAlert(false);
    setAlertType('alert');
  };

  const handleConfirmation = () => {
    setShowAlert(false);
    setConfirmationButtonType(false);
    setAlertType('alert');
  };

  // Filter and sort organizations based on search term, region, type, and sorting criteria
  const filteredOrganizations = useMemo(() => {
    let filtered = organizations.filter((org: any) => {
      const matchesSearch = org.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           org.code.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesRegion = selectedRegion === '' || org.region === selectedRegion;
      const matchesType = selectedType === '' || org.type === selectedType;
      
      return matchesSearch && matchesRegion && matchesType;
    });

    // Sort the filtered results
    filtered.sort((a: any, b: any) => {
      let aValue, bValue;
      
      switch (sortBy) {
        case 'name':
          aValue = a.name.toLowerCase();
          bValue = b.name.toLowerCase();
          break;
        case 'region':
          aValue = a.region;
          bValue = b.region;
          break;
        case 'type':
          aValue = a.type;
          bValue = b.type;
          break;
        case 'code':
          aValue = a.code;
          bValue = b.code;
          break;
        case 'metrics':
          aValue = a.observabilityMetrics?.metricsCount || 0;
          bValue = b.observabilityMetrics?.metricsCount || 0;
          break;
        case 'logs':
          aValue = a.observabilityMetrics?.logsCount || 0;
          bValue = b.observabilityMetrics?.logsCount || 0;
          break;
        case 'traces':
          aValue = a.observabilityMetrics?.tracesCount || 0;
          bValue = b.observabilityMetrics?.tracesCount || 0;
          break;
        default:
          return 0;
      }

      // Handle numeric vs string comparison
      let comparison = 0;
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        comparison = aValue - bValue;
      } else {
        comparison = String(aValue).localeCompare(String(bValue));
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [organizations, searchTerm, selectedRegion, selectedType, sortBy, sortOrder]);

  // Get unique regions and types for filter dropdowns
  const availableRegions = useMemo(() => {
    const regions = Array.from(new Set<string>(organizations.map((org: any) => org.region)));
    return regions.sort();
  }, [organizations]);

  const availableTypes = useMemo(() => {
    const types = Array.from(new Set<string>(organizations.map((org: any) => org.type)));
    return types.sort();
  }, [organizations]);

  return (
    <div className="page-container">
      <div className="manage-orgs-header">
        <h1>Manage Your Organizations</h1>
        <p>You can view, edit, and regenerate tokens for your existing organizations below.</p>
      </div>
      
      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search by name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={selectedRegion}
            onChange={(e) => setSelectedRegion(e.target.value)}
            className="filter-select"
          >
            <option value="">All Regions</option>
            {availableRegions.map((region) => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
          
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="filter-select"
          >
            <option value="">All Types</option>
            {availableTypes.map((type) => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <div className="sort-controls">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="name">Sort by Name</option>
              <option value="region">Sort by Region</option>
              <option value="type">Sort by Type</option>
              <option value="code">Sort by Code</option>
              <option value="metrics">Sort by Metrics</option>
              <option value="logs">Sort by Logs</option>
              <option value="traces">Sort by Traces</option>
            </select>
            
            <button
              className="sort-order-button"
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              title={`Currently sorting ${sortOrder === 'asc' ? 'ascending' : 'descending'}`}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>
          
          {(searchTerm || selectedRegion || selectedType) && (
            <button 
              className="clear-filters-button"
              onClick={() => {
                setSearchTerm('');
                setSelectedRegion('');
                setSelectedType('');
              }}
            >
              Clear Filters
            </button>
          )}
        </div>
      </div>

      {loading && <p>Loading...</p>}
      {!loading && organizations.length === 0 && <p>No organizations found.</p>}
      {!loading && organizations.length > 0 && filteredOrganizations.length === 0 && (
        <p>No organizations match your search criteria.</p>
      )}

      {!loading && organizations.length > 0 && (
        <div className="results-info">
          <span>
            Showing {filteredOrganizations.length} of {organizations.length} organizations
          </span>
        </div>
      )}

      <ul className="organization-list">
        {filteredOrganizations.map((org) => (
          <li key={org.id} className="organization-item">
            <div className="organization-details">
              <strong>{org.name}</strong> <br />
              <span>Region:</span> {org.region} <br />
              <span>Type:</span> {org.type} <br />
              <span>X-Scope-OrgId:</span> {org.code} <br />
              {org.leanix_cost_id && <><span>LeanIX Cost ID:</span> {org.leanix_cost_id} <br /></>}
              {org.observabilityMetrics && (
                <div className="observability-metrics">
                  <br />
                  <strong>Observability Data:</strong>
                  <div className="metrics-row">
                    <span>Metrics: {org.observabilityMetrics.metricsCount.toLocaleString()}</span>
                    <span>Logs: {org.observabilityMetrics.logsCount.toLocaleString()}</span>
                    <span>Traces: {org.observabilityMetrics.tracesCount.toLocaleString()}</span>
                  </div>
                </div>
              )}
            </div>
            <button
              className="edit-org-button"
              onClick={() => navigate(`/edit-organization/${org.id}`)}
              title="Edit Organization"
            >
              Edit
            </button>
            <div className="token-action-group">
              <button
                className="regenerate-token-button"
                onClick={() => {
                  setSelectedOrgId(org.id);
                  setAlertMessage(`Are you sure you want to regenerate the token for ${org.name}?`);
                  setConfirmationButtonType(false);
                  setShowAlert(true);
                }}
                title="Regenerate Token"
              >
                Regenerate Token
              </button>
              <div className="token-disclaimer">
                <span className="token-disclaimer-symbol">⚠</span> Contact the O11y team for a new token—regenerating it here will invalidate the current one.
              </div>
            </div>
          </li>
        ))}
      </ul>

      <AlertBox
        show={showAlert}
        message={alertMessage}
        onClose={confirmationButtonType ? handleConfirmation : handleAlertNo}
        onConfirm={!confirmationButtonType ? handleAlertYes : undefined}
        type={alertType}
      />
    </div>
  );
}

export default ManageOrganizations;